import React, { useEffect, useRef, useState, useCallback } from 'react'

const TIMEOUT_MS     = 15 * 60 * 1000   // 15 minutes idle — HIPAA requirement
const WARNING_MS     =  2 * 60 * 1000   // warn 2 minutes before logout
const TICK_MS        = 30 * 1000        // check every 30 s (keeps timer lightweight)
const TAB_CLOSE_MS   =  5 * 60 * 1000   // 5 minutes — log out if tab was closed/hidden this long
const TAB_HIDDEN_KEY = 'cp_tab_hidden_at'

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

// Events that count as "active"
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click']

export default function SessionTimeoutManager({ onLogout, user }) {
  const lastActivityRef  = useRef(Date.now())
  const tickerRef        = useRef(null)
  const [showWarning,    setShowWarning]    = useState(false)
  const [secondsLeft,    setSecondsLeft]    = useState(Math.round(WARNING_MS / 1000))
  const countdownRef     = useRef(null)

  //  Reset activity timestamp on any user interaction 
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now()
    if (showWarning) {
      setShowWarning(false)
      clearInterval(countdownRef.current)
    }
  }, [showWarning])

  //  Start countdown display when warning is shown 
  const startCountdown = useCallback(() => {
    setSecondsLeft(Math.round(WARNING_MS / 1000))
    clearInterval(countdownRef.current)
    countdownRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(countdownRef.current)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }, [])

  // Tab-close / background timeout — log out if tab was hidden for more than 5 minutes.
  // Covers: closing the tab, closing the laptop, switching away for a long time.
  useEffect(() => {
    if (!user) return

    // On mount: if the tab was previously hidden and 5+ minutes have passed, log out now.
    const hiddenAt = localStorage.getItem(TAB_HIDDEN_KEY)
    if (hiddenAt && Date.now() - parseInt(hiddenAt, 10) >= TAB_CLOSE_MS) {
      localStorage.removeItem(TAB_HIDDEN_KEY)
      onLogout()
      return
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Tab went to background — record the time
        localStorage.setItem(TAB_HIDDEN_KEY, Date.now().toString())
      } else {
        // Tab came back — check how long it was gone
        const t = localStorage.getItem(TAB_HIDDEN_KEY)
        if (t && Date.now() - parseInt(t, 10) >= TAB_CLOSE_MS) {
          localStorage.removeItem(TAB_HIDDEN_KEY)
          onLogout()
        } else {
          localStorage.removeItem(TAB_HIDDEN_KEY)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [user, onLogout])

  useEffect(() => {
    if (!user) return   // don't run timer when logged out

    // Attach activity listeners
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }))

    // Main tick — checks elapsed idle time
    tickerRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current
      if (idle >= TIMEOUT_MS) {
        // Time's up — force logout
        clearInterval(tickerRef.current)
        clearInterval(countdownRef.current)
        setShowWarning(false)
        onLogout()
      } else if (idle >= TIMEOUT_MS - WARNING_MS && !showWarning) {
        setShowWarning(true)
        startCountdown()
      }
    }, TICK_MS)

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, resetTimer))
      clearInterval(tickerRef.current)
      clearInterval(countdownRef.current)
    }
  }, [user, resetTimer, showWarning, onLogout, startCountdown])

  if (!showWarning) return null

  const mins = Math.floor(secondsLeft / 60)
  const secs = String(secondsLeft % 60).padStart(2, '0')
  const timeDisplay = mins > 0 ? `${mins}:${secs}` : `0:${secs}`

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999, fontFamily: FONT,
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '36px 40px',
        maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        textAlign: 'center',
      }}>
        {/* Clock icon */}
        <div style={{ fontSize: '48px', lineHeight: 1, marginBottom: '16px' }}>⏱</div>

        <h2 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
          Session Expiring Soon
        </h2>
        <p style={{ margin: '0 0 6px', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
          For patient data security, you'll be automatically logged out due to inactivity.
        </p>

        {/* Countdown */}
        <div style={{
          margin: '20px 0', fontSize: '42px', fontWeight: '800',
          color: secondsLeft <= 30 ? '#dc2626' : '#7c3aed',
          letterSpacing: '-1px', transition: 'color 0.3s',
        }}>
          {timeDisplay}
        </div>

        <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#94a3b8' }}>
          Move your mouse or press any key to stay logged in
        </p>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={resetTimer}
            style={{
              background: '#7c3aed', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '10px 28px', fontSize: '15px',
              fontWeight: '600', cursor: 'pointer', flex: 1, maxWidth: '200px',
              fontFamily: FONT,
            }}
          >
            Stay Logged In
          </button>
          <button
            onClick={onLogout}
            style={{
              background: '#f1f5f9', color: '#64748b', border: 'none',
              borderRadius: '8px', padding: '10px 20px', fontSize: '15px',
              fontWeight: '500', cursor: 'pointer', fontFamily: FONT,
            }}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  )
}
